import React, { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getProfessorToken } from '../authCookie';
import { apiErrorText } from '@/lib/apiError';

interface ReviewSessionSummary {
  session_id: string;
  session_timecreated: string;
  ended_at?: string | null;
  is_active: boolean;
}

interface PromptReport {
  prompt_id: string;
  prompt_text: string;
  position: number;
  pct25?: number | null;
  median?: number | null;
  pct75?: number | null;
  respondents?: number | null;
}

interface ReportQuestion {
  question_id: string;
  question_text: string;
  votes: number;
}

interface SessionReport {
  session_id: string;
  session_timecreated: string;
  ended_at?: string | null;
  prompts: PromptReport[];
  questions: ReportQuestion[];
}

function authHeaders(): Record<string, string> {
  const t = getProfessorToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function stat(v?: number | null): string {
  return v == null ? '–' : String(v);
}

export default function ReviewHistoryDialog({
  classId,
  classLabel,
  backendUrl,
}: {
  classId: number;
  classLabel: string;
  backendUrl?: string;
}) {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<ReviewSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState<SessionReport | null>(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      setReport(null);
      try {
        const res = await fetch(`${backendUrl}/api/classes/${classId}/review-sessions`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success) setSessions(data.sessions);
        else setError(apiErrorText(data?.detail, 'Failed to load review sessions.'));
      } catch {
        if (!cancelled) setError('Unable to load review sessions.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, classId, backendUrl]);

  const openReport = async (sessionId: string) => {
    setIsReportLoading(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/sessions/${sessionId}/report`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(apiErrorText(data?.detail, 'Failed to load the report.'));
        return;
      }
      setReport(data);
    } catch {
      setError('Unable to load the report.');
    } finally {
      setIsReportLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="lg" className="w-full">
          Past review sessions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle>Review sessions</DialogTitle>
          <DialogDescription>{classLabel}</DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {report ? (
          <div>
            <Button variant="ghost" size="sm" className="mb-1 -ml-2" onClick={() => setReport(null)}>
              <ChevronLeft aria-hidden /> Back
            </Button>
            <p className="text-sm text-muted-foreground">
              Started {fmtDate(report.session_timecreated)}
              {report.ended_at ? ` · ended ${fmtDate(report.ended_at)}` : ' · still open'}
            </p>

            <h3 className="mt-4 text-sm font-semibold">Understanding by topic</h3>
            {report.prompts.length === 0 ? (
              <p className="mt-1 text-sm text-muted-foreground">No prompts were posted.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {report.prompts.map((p) => (
                  <li key={p.prompt_id} className="rounded-lg border p-3">
                    <p className="text-sm font-medium">{p.prompt_text}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {p.respondents ? `${p.respondents} response${p.respondents === 1 ? '' : 's'}` : 'No responses'}
                      {' · '}p25 {stat(p.pct25)} · median {stat(p.median)} · p75 {stat(p.pct75)}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mt-5 text-sm font-semibold">Questions</h3>
            {report.questions.length === 0 ? (
              <p className="mt-1 text-sm text-muted-foreground">No questions were submitted.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {[...report.questions].sort((a, b) => b.votes - a.votes).map((q) => (
                  <li key={q.question_id} className="flex items-start gap-2 rounded-lg border p-3">
                    <span className="shrink-0 text-xs font-semibold text-muted-foreground">{q.votes}▲</span>
                    <span className="text-sm">{q.question_text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No review sessions for this class yet.</p>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li key={s.session_id}>
                <button
                  type="button"
                  onClick={() => openReport(s.session_id)}
                  disabled={isReportLoading}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted"
                >
                  <span className="text-sm">
                    Started {fmtDate(s.session_timecreated)}
                  </span>
                  <Badge variant={s.is_active ? 'success' : 'outline'}>
                    {s.is_active ? 'Open' : 'Ended'}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
